import localforage from "localforage";

import * as Storage from "./storage";

const MATOMO_URL = "https://analytics.bywaters.co.uk/";

let currentUserId = "";
let lastTrackedUrl = "";
let forceNewVisit = false;

// Commands pushed before matomo.js has loaded are queued and replayed by it
const paq = () => (window._paq = window._paq || []);

const normaliseUserId = (userId: string) => userId.trim().toLowerCase();

// Hash routes can carry access tokens / reset codes in the query string, so only the path is kept
const currentPagePath = () =>
  window.location.hash.replace(/^#/, "").split("?")[0] || "/";

export const initMatomo = async () => {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/matomo_site.json`);
    if (!res.ok) throw new Error("Failed to load matomo_site.json");
    const config = await res.json();
    const siteId = String(config.siteId ?? "");
    // Not substituted by the pipeline (e.g. local dev), so nothing to report to
    if (!/^\d+$/.test(siteId)) return;

    // Restore the user after a page refresh within an authenticated session
    if (await Storage.TOKEN.get()) {
      const userData = await localforage.getItem<{ email?: string }>("userData");
      if (userData?.email) {
        currentUserId = normaliseUserId(userData.email);
        paq().push(["setUserId", currentUserId]);
      }
    }

    paq().push(["setTrackerUrl", MATOMO_URL + "matomo.php"]);
    paq().push(["setSiteId", siteId]);
    paq().push(["enableLinkTracking"]);

    const script = document.createElement("script");
    script.async = true;
    script.src = MATOMO_URL + "matomo.js";
    document.head.appendChild(script);
  } catch (err) {
    console.error("Matomo initialisation failed:", err);
  }
};

// Uses virtual URLs without the hash, so reports don't depend on Matomo keeping URL fragments
export const trackPageView = (path: string, title: string) => {
  const url = window.location.origin + path;
  if (url === lastTrackedUrl) return;
  if (lastTrackedUrl) paq().push(["setReferrerUrl", lastTrackedUrl]);
  lastTrackedUrl = url;
  paq().push(["setCustomUrl", url]);
  paq().push(["setDocumentTitle", title]);
  paq().push(["trackPageView"]);
  if (forceNewVisit) {
    paq().push(["appendToTrackingUrl", ""]);
    forceNewVisit = false;
  }
};

export const setMatomoUser = (userId: string, loginMethod: string) => {
  if (!userId) return;
  currentUserId = normaliseUserId(userId);
  paq().push(["setUserId", currentUserId]);
  paq().push(["trackEvent", "Authentication", "Login", loginMethod]);
};

// Ends the user's visit so whatever happens after logout isn't attributed to them
export const clearMatomoUser = () => {
  if (!currentUserId) return;
  currentUserId = "";
  paq().push(["resetUserId"]);
  paq().push(["appendToTrackingUrl", "new_visit=1"]);
  forceNewVisit = true;
};

// Shows up in Behaviour > Downloads as <origin>/<page path>/<source>/<file name>
export const trackDownload = (fileName: string, source: string) => {
  const safeFileName = fileName.replace(/[/\\?#]/g, "_");
  paq().push([
    "trackLink",
    `${window.location.origin}${currentPagePath()}/${source}/${safeFileName}`,
    "download",
  ]);
};
